// moderationManagment — chat en direct
//
// L'IA de Google (customer-api, GoogleChatModerator) note chaque message de
// 0 à 1 à l'envoi. Selon le niveau de la chaîne, elle le publie, le refuse,
// ou le laisse « à valider » : c'est la file de l'écran Modération.

export type ChatMessageStatus = 'approved' | 'pending' | 'rejected';

export type ChatDecision = 'approve' | 'reject';

export type ChatSanction = 'kick' | 'ban';

export interface ChatModChannel {
  id            : number;
  name          : string;
  publicRef     : string | null;
  slug          : string | null;
  hasLogo       : boolean;
  /** Chaîne allumée (intention en base, pas l'état du conteneur). */
  active        : boolean;
  /** Les spectateurs peuvent écrire. */
  chatOpen      : boolean;
  level         : ChatModerationLevel;
  pending       : number;
  oldestPending : string | null;
  approvedHour  : number;
  rejectedHour  : number;
  byAiHour      : number;
  totalHour     : number;
  /** Spectateurs connectés au salon. */
  present       : number;
}

export interface ChatModMessage {
  id                : number;
  channelId         : number;
  ownerId           : number | null;
  pseudo            : string;
  text              : string;
  creationDate      : string | null;
  status            : ChatMessageStatus;
  /** 0 à 1 ; null = l'IA n'a pas répondu. */
  aiScore           : number | null;
  aiCategories      : Record<string, number>;
  aiFlags           : string[];
  aiEngine          : 'google' | 'claude' | 'aucun' | null;
  aiReason          : string | null;
  moderatedBy       : 'ai' | 'admin' | null;
  moderationDate    : string | null;
  /** 'admin' exclu, 'ban' banni, 'user' parti, null encore dans le salon. */
  authorState       : 'admin' | 'ban' | 'user' | null;
  authorTotal24h    : number;
  authorRejected24h : number;
}

export interface ChatModMessages {
  pending    : ChatModMessage[];
  recent     : ChatModMessage[];
  serverTime : string;
}

export interface ChatModBan {
  id      : number;
  pseudo  : string | null;
  userId  : number | null;
  /** Masquée par le serveur (« 82.64.x.x »). */
  ip      : string;
  banDate : string | null;
}

export type ChatModerationLevel = 0 | 1 | 2 | 3;

export interface ChatModerationLevelInfo {
  value   : ChatModerationLevel;
  label   : string;
  hint    : string;
  /** Seuils de GoogleChatModerator (customer-api), à garder identiques. */
  publish : number | null;
  reject  : number | null;
}

export const CHAT_MODERATION_LEVELS: ChatModerationLevelInfo[] = [
  { value: 0, label: 'Légère',   hint: "L'IA ne bloque que l'extrême",          publish: 0.85, reject: 0.95 },
  { value: 1, label: 'Standard', hint: 'Les cas douteux vous sont soumis',      publish: 0.35, reject: 0.80 },
  { value: 2, label: 'Avancée',  hint: 'Au moindre doute, vous tranchez',       publish: 0.15, reject: 0.60 },
  { value: 3, label: 'Manuelle', hint: "Tout passe par vous, l'IA trie la file", publish: null, reject: null },
];

/** Noms des catégories de Google Cloud Natural Language, en français. */
export const CHAT_CATEGORY_LABELS: Record<string, string> = {
  'Toxic'                 : 'Toxique',
  'Insult'                : 'Insulte',
  'Derogatory'            : 'Haine',
  'Sexual'                : 'Sexuel',
  'Violent'               : 'Violence',
  'Death, Harm & Tragedy' : 'Drame',
  'Illicit Drugs'         : 'Drogues',
  'Profanity'             : 'Grossier',
  'Firearms & Weapons'    : 'Armes',
  'Public Safety'         : 'Sécurité',
  'Health'                : 'Santé',
  'Religion & Belief'     : 'Religion',
  'War & Conflict'        : 'Guerre',
  'Politics'              : 'Politique',
  'Finance'               : 'Finance',
  'Legal'                 : 'Juridique',
};

/** Sujets sensibles : affichés, mais ils ne rendent pas un message douteux. */
export const CHAT_SENSITIVE_CATEGORIES = new Set([
  'Health', 'Religion & Belief', 'War & Conflict', 'Politics', 'Finance', 'Legal',
]);

/** Signaux repérés hors IA (liens, coordonnées…). */
export const CHAT_FLAG_LABELS: Record<string, string> = {
  lien        : 'Lien',
  coordonnees : 'Coordonnées',
  majuscules  : 'Majuscules',
  repetition  : 'Flood',
};
